import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { useEditorContext } from '@editor/contexts/Editor'
import { PointArray } from '@editor/interfaces/attribute'
import { PolygonPlaneMeshProps, PolylineProps } from '@editor/interfaces/canvas'
import { CanvasConfig, CanvasEventsExtra } from '@editor/interfaces/editor'
import { Polygon } from '@editor/interfaces/shape'
import { setDisablePanning, setDisableRotation } from '@editor/pages/projects/editor/store/editor'
import { getPlaneQuadInBox } from '@editor/services/Plane'
import { getCentroidOfPolygon3d, getRotationDirection, projectToScreen } from '@editor/services/Points'
import { sortRectanglePoints3D } from '@editor/services/Shape'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { setCursor } from '@editor/store/tool'
import { CircleAnchorIcon } from '@infraloop/common'
import { Box3, Plane, Raycaster, Vector2, Vector3 } from 'three'

import {
  SelectionStage,
  addPlanePoint,
  setPlanePoints,
  setPreviewPlanePoints,
  setSelectionStage,
  undoLastPlanePoint,
} from '../store'

const raycaster = new Raycaster()

const ROTATION_SENSITIVITY = 0.0025

export default function useMainCanvas(): CanvasConfig {
  // Context
  const { cameraRef } = useEditorContext()

  // Store
  const dispatch = useAppDispatch()

  const pcdBoundingBox = useAppSelector((state) => state.editor.pcdBoundingBox)
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const editingElementId = useAppSelector((state) => state.toolUtil.core.editingElementId)

  const selectionStage = useAppSelector((state) => state.toolCameraProfile.selectionStage)
  const drawnPlanePoints = useAppSelector((state) => state.toolCameraProfile.drawnPlanePoints)
  const previewPlanePoints = useAppSelector((state) => state.toolCameraProfile.previewPlanePoints)

  // State
  const [plane, setPlane] = useState<Plane>()
  const [hasCurrentPosition, setHasCurrentPosition] = useState<boolean>(false)

  // Refs
  const lastTouchPointRef = useRef<PointArray>(undefined)
  const previousScreenPosRef = useRef<{ x: number; y: number } | null>(null)
  const is2FingerTouchedRef = useRef<boolean>(false)
  const isMouseDownRef = useRef<boolean>(false)
  const fineTuneTarget = useRef<Vector3>(new Vector3())
  const fineTuneDirection = useRef<Vector3>(new Vector3())

  // Vars
  const isToolSelected = useMemo(() => selectedTool === EDITOR_TOOLS.CAMERA_PROFILE, [selectedTool])

  // Checks element's state
  const isEditing = useCallback((id: string) => editingElementId === id, [editingElementId])

  /**
   * Handle mouse up event.
   *
   * @param e MouseEvent
   * @param point Clicked point in 3D Space
   */
  const onMouseUp = useCallback(
    (
      me: React.MouseEvent<HTMLDivElement> | undefined,
      te: React.TouchEvent<HTMLDivElement> | undefined,
      point: PointArray | undefined,
      { isDragged }: CanvasEventsExtra,
    ) => {
      // ## Draw stage only
      if (!isToolSelected || (!me && !te) || selectionStage !== SelectionStage.DRAW || !point || isDragged) return

      const isTouched = !!te
      if (drawnPlanePoints.length < 3) {
        dispatch(addPlanePoint({ point, isTouched }))

        if (drawnPlanePoints.length + 1 === 3) {
          dispatch(setSelectionStage(SelectionStage.ROTATE))
        }
      }
    },
    [isToolSelected, drawnPlanePoints, selectionStage, dispatch],
  )

  /**
   * Fine tune positioning of the camera.
   * Rotate camera around the target while being parallel on the plane.
   */
  const onFocusFineTune = useCallback(
    (screenPos: { x: number; y: number }) => {
      const canvasBox = document.getElementById('canvasBox') as HTMLElement
      if (!cameraRef.current || !plane || !previousScreenPosRef.current || !canvasBox) return

      // Define some stuff for later
      const camera = cameraRef.current
      camera.getWorldDirection(fineTuneDirection.current)

      // Get target by raycasting camera onto plane
      raycaster.ray.set(camera.position, fineTuneDirection.current)
      const found = raycaster.ray.intersectPlane(plane, fineTuneTarget.current)
      if (!found) return

      // ## Do panning
      if (is2FingerTouchedRef.current) {
        return
      }

      // ## Otherwise, do rotation

      // Get target in screen space
      const canvasRect = canvasBox.getBoundingClientRect()
      const targetScreen = projectToScreen(fineTuneTarget.current, camera, canvasRect.width, canvasRect.height)

      // if mouse is higher than the target, rotate in the opposite direction
      // also if mouse is on the left side of the target, rotate in the opposite direction
      // Negative because it's the opposite of what three-stdlib needs.
      const sign = -getRotationDirection(
        new Vector2(targetScreen.x, targetScreen.y),
        new Vector2(previousScreenPosRef.current.x, previousScreenPosRef.current.y),
        new Vector2(screenPos.x, screenPos.y),
      )

      // calculate total distance travelled by mouse
      const delta =
        new Vector2(screenPos.x, screenPos.y).distanceTo(
          new Vector2(previousScreenPosRef.current.x, previousScreenPosRef.current.y),
        ) * sign

      // Calculate the rotation angle based on delta movement and sensitivity
      const angle = delta * ROTATION_SENSITIVITY

      camera.rotateZ(angle)
      camera.matrix.compose(camera.position, camera.quaternion, camera.scale)

      previousScreenPosRef.current = { x: screenPos.x, y: screenPos.y }
    },
    [cameraRef, plane],
  )

  /**
   * Undo the last action.
   * 1. When drawing, go back a point.
   * 2. If completed, re-open (from ROTATE to DRAW stage).
   * 3. If on FOCUS stage, go back to ROTATE stage.
   */
  const onUndo = useCallback(() => {
    if (!isToolSelected) return

    if (selectionStage === SelectionStage.DRAW) {
      dispatch(undoLastPlanePoint())
    } else if (selectionStage === SelectionStage.ROTATE) {
      dispatch(setSelectionStage(SelectionStage.DRAW))
      dispatch(undoLastPlanePoint())
      dispatch(setPreviewPlanePoints([]))
      setPlane(undefined)
    } else if (selectionStage === SelectionStage.FOCUS) {
      dispatch(setSelectionStage(SelectionStage.ROTATE))
      dispatch(setDisablePanning(false))
      dispatch(setDisableRotation(false))
    }
  }, [isToolSelected, selectionStage, dispatch])

  /**
   * Once we have enough points, calculate the plane.
   */
  useEffect(() => {
    if (drawnPlanePoints.length < 3 || selectionStage === SelectionStage.DRAW || !pcdBoundingBox) return

    // make plane and find out plane that extends the entire PCD bounding box
    const vectorPoints = drawnPlanePoints.map((p) => new Vector3(...p))
    const pln = new Plane().setFromCoplanarPoints(vectorPoints[0], vectorPoints[1], vectorPoints[2])

    setPlane(pln)

    const planeQuad = getPlaneQuadInBox(
      new Box3(new Vector3(...pcdBoundingBox[0]), new Vector3(...pcdBoundingBox[1])),
      pln,
    )
    if (planeQuad) dispatch(setPreviewPlanePoints(sortRectanglePoints3D(planeQuad).map((p) => p.toArray())))
  }, [drawnPlanePoints, selectionStage, pcdBoundingBox, dispatch])

  /**
   * Handle mouse up event on document level.
   * Mainly used by FOCUS stage to detect mouse up outside of canvas.
   */
  useEffect(() => {
    const mouseUp = () => {
      previousScreenPosRef.current = null
      isMouseDownRef.current = false

      // If we're on FOCUS stage, mouse up can mean done with dragging, so change cursor back to GRAB.
      if (selectionStage === SelectionStage.FOCUS) {
        dispatch(setCursor(CursorState.GRAB))
      }
    }

    document.addEventListener('mouseup', mouseUp)

    return () => {
      document.removeEventListener('mouseup', mouseUp)
    }
  }, [selectionStage, dispatch])

  /**
   * Drawn plane distance labels.
   * Also holds interaction events for moving the anchor.
   */
  const drawnPlaneAnchors = useMemo((): PolylineProps => {
    const isComplete = drawnPlanePoints.length >= 3

    return {
      id: `camera-focus-plane-distance-label`,
      points: drawnPlanePoints,
      isWorking: !isComplete,
      isEditing: isEditing(`camera-focus-plane-distance-label`),
      isInteractable: false,
      hideAnchors: true,
      hideBottomAnchor: !hasCurrentPosition && !isComplete,
      line: {
        visible: false,
      },
      anchorProps: {
        outlineOpacity: 0,
        color: '#248CE4',
        scale: 1.3,
        icon: CircleAnchorIcon.MOVE,
        iconScale: 1.1,
        iconColor: 'white',
        tooltipText: isComplete ? '点を移動' : undefined,
      },
      vertexAnchors: {
        isInteractable: isComplete,
        onUpdate: (points) => {
          if (isComplete) dispatch(setPlanePoints(points))
        },
        onUpdated: (points) => {
          dispatch(setPlanePoints(points))
        },
      },
    }
  }, [drawnPlanePoints, hasCurrentPosition, isEditing, dispatch])

  /**
   * Plane mesh for preview.
   */
  const previewPlaneMesh = useMemo((): PolygonPlaneMeshProps[] => {
    if (previewPlanePoints.length < 4 || selectionStage === SelectionStage.DRAW) return []

    return [
      {
        id: 'camara-profile-preview-plane',
        polygon: {
          shape_id: 'camara-profile-preview-plane',
          positions: previewPlanePoints,
          center: getCentroidOfPolygon3d(previewPlanePoints.map((p) => new Vector3(...p))).toArray(),
          vertices: previewPlanePoints.map((p) => [p[0], p[1]]),
          transformation: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        } as Polygon,
        opacity: selectionStage === SelectionStage.FOCUS ? 0.25 : 0.5,
      },
    ]
  }, [previewPlanePoints, selectionStage])

  return {
    objects: {
      polylines:
        [SelectionStage.FOCUS, SelectionStage.SAVING].includes(selectionStage) || !isToolSelected
          ? []
          : [drawnPlaneAnchors],
      polygonPlaneMeshes: [...previewPlaneMesh],
    },
    events: {
      onTouchStart: (ev, point) => {
        if (!isToolSelected) return
        is2FingerTouchedRef.current = ev.touches.length > 1
        lastTouchPointRef.current = point
        previousScreenPosRef.current = { x: ev.touches[0].clientX, y: ev.touches[0].clientY }
      },
      onTouchEnd: (ev, extra) => {
        if (!isToolSelected) return
        onMouseUp(undefined, ev, lastTouchPointRef.current, extra)
      },
      onMouseUp: (ev, point, meshPoint, extra) => {
        if (!isToolSelected) return

        // ignore if we have a touch point, we'll handle it in onTouchEnd
        if (lastTouchPointRef.current) {
          lastTouchPointRef.current = undefined
          return
        }

        onMouseUp(ev, undefined, meshPoint || point, extra)
        isMouseDownRef.current = false
      },
      onMouseDown: (me) => {
        const screenPos = {
          x: me.clientX,
          y: me.clientY,
        }
        previousScreenPosRef.current = { x: screenPos.x, y: screenPos.y }
        if (me.button === 0) isMouseDownRef.current = true
      },
      onMove: useCallback(
        (
          point?: PointArray,
          solidMeshPoint?: PointArray,
          me?: React.MouseEvent<HTMLDivElement, MouseEvent>,
          te?: React.TouchEvent<HTMLDivElement>,
        ) => {
          if (!isToolSelected || (!me && !te)) return

          if ([SelectionStage.DRAW, SelectionStage.ROTATE].includes(selectionStage))
            setHasCurrentPosition(!!(solidMeshPoint || point))

          const screenPos = {
            x: te?.touches[0].clientX || me!.clientX,
            y: te?.touches[0].clientY || me!.clientY,
          }

          // Fine-tuning position of the camera
          if (
            selectionStage === SelectionStage.FOCUS &&
            previousScreenPosRef.current &&
            ((me && isMouseDownRef.current) || te)
          ) {
            onFocusFineTune(screenPos)
          }

          previousScreenPosRef.current = { x: screenPos.x, y: screenPos.y }

          // Beyond this, we need a point
          if (!point) return

          if (te) {
            lastTouchPointRef.current = point
          }
        },
        [isToolSelected, selectionStage, onFocusFineTune],
      ),
      onUndo,
    },
  }
}
